import { useEffect, useState } from "react";
import { useAction, useTrouble } from "../App";
import { api } from "../api/client";
import type { AccountView, BotView, SecretView } from "../api/types";
import { Apparatus, Empty, RuledRow, Tag, wordFor } from "../components/Parts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { grouped, now as rightNow } from "../lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SECRETS, secretLabel, secretWords } from "../lib/secrets";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/*
 * "No set" has to travel as a value, because a select item cannot be the empty string: Radix uses
 * that to mean "nothing is selected" and throws on it. The wire still carries null, so the sentinel
 * is translated on the way in and on the way out and never leaves this file.
 */
const SHARED = "the-shared-account";

/** Every bot the dashboard knows, and what its lane has been doing. */
export function Bots({ navigate }: { navigate: (to: string) => void }) {
  const [bots, setBots] = useState<BotView[] | null>(null);
  const [accounts, setAccounts] = useState<AccountView[]>([]);
  const [secrets, setSecrets] = useState<SecretView[]>([]);
  const report = useTrouble();

  // Asked once: the accounts come from the environment, so they cannot change while the page is open.
  useEffect(() => {
    let live = true;
    api
      .accounts()
      .then((it) => live && setAccounts(it))
      .catch(report);
    return () => {
      live = false;
    };
  }, [report]);

  // Asked once as well, and replaced in place by whatever a write answers with.
  useEffect(() => {
    let live = true;
    api
      .secrets()
      .then((it) => live && setSecrets(it))
      .catch(report);
    return () => {
      live = false;
    };
  }, [report]);

  useEffect(() => {
    let live = true;
    const load = () =>
      api
        .bots()
        .then((it) => live && setBots(it))
        .catch(report);

    void load();
    // Bots never refreshed in the Compose version, so a lane could say "idle" while a run was going.
    // Slower than Now's two seconds, because a bot's own facts change slowly.
    const timer = window.setInterval(() => void load(), 10_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [report]);

  if (!bots) return <Empty>Reading which bots are registered.</Empty>;

  return (
    <>
      <h1 className="font-serif text-title font-medium -tracking-[0.015em]">
        Bots
      </h1>
      {bots.length === 0 ? (
        <Empty>
          No bot is registered. A bot is a module implementing one interface.
        </Empty>
      ) : (
        bots.map((bot) => (
          <BotCard
            key={bot.id}
            bot={bot}
            accounts={accounts}
            navigate={navigate}
          />
        ))
      )}

      {secrets.length === 0 ? null : (
        <>
          <h2 className="font-serif text-heading font-medium">Credentials</h2>
          {secrets.map((secret) => (
            <SecretCard
              key={secret.name}
              secret={secret}
              onSet={(it) =>
                setSecrets((all) =>
                  all.map((one) => (one.name === it.name ? it : one)),
                )
              }
            />
          ))}
        </>
      )}
    </>
  );
}

/**
 * One secret an operator can replace without a redeploy.
 *
 * The value is write-only: what comes back from a write is its length and when, so a reloaded screen can
 * say a cookie is there and never show it. The field is cleared on a successful write for the same reason.
 */
function SecretCard({
  secret,
  onSet,
}: {
  secret: SecretView;
  onSet: (secret: SecretView) => void;
}) {
  const [busy, act] = useAction();
  const [typed, setTyped] = useState("");
  const id = `secret-${secret.name}`;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <Label htmlFor={id} className="text-heading font-medium">
            {secretLabel(secret.name)}
          </Label>
          <Tag>{secretWords(secret, rightNow())}</Tag>
        </div>

        <p className="text-muted-foreground max-w-prose">
          {SECRETS[secret.name]?.help}
        </p>

        <div className="flex items-center gap-2.5 max-sm:flex-col max-sm:items-stretch">
          <Input
            id={id}
            type="password"
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
            placeholder={secret.set ? "paste a new one" : "paste it here"}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
          <Button
            disabled={busy || typed.trim() === ""}
            onClick={() =>
              act(async () => {
                onSet(await api.setSecret(secret.name, typed.trim()));
                setTyped("");
              })
            }
          >
            {busy ? "Saving" : "Save"}
          </Button>
          {secret.set && !secret.fromEnvironment ? (
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() =>
                act(async () => onSet(await api.clearSecret(secret.name)))
              }
            >
              Clear
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function BotCard({
  bot,
  accounts,
  navigate,
}: {
  bot: BotView;
  accounts: AccountView[];
  navigate: (to: string) => void;
}) {
  const [starting, act] = useAction();
  const [pointing, point] = useAction();
  const fields = bot.options.fields ?? [];

  return (
    <Card>
      <CardContent className="flex items-start gap-8 max-sm:flex-col max-sm:gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <div className="flex items-center gap-2.5">
            <h2 className="font-serif text-lane font-medium">{bot.name}</h2>
            <span className="font-mono text-mono text-muted-foreground">
              {bot.id}
            </span>
          </div>
          <p className="m-0 max-w-[60ch] text-pretty text-muted-foreground">
            {bot.description}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Tag>
              {fields.length} option{fields.length === 1 ? "" : "s"}
            </Tag>
            <Tag>{bot.hasApprovalGate ? "approval gate" : "no gate"}</Tag>
            <Tag>edits as {bot.account ?? "the shared account"}</Tag>
          </div>

          {/* Only worth showing once there is somewhere else to point it. An environment with one
              account has nothing to choose between. */}
          {accounts.length > 0 ? (
            <div className="flex items-center gap-3">
              <Label htmlFor={`account-${bot.id}`} className="apparatus">
                Account
              </Label>
              <Select
                disabled={pointing}
                value={bot.accountSet ?? SHARED}
                onValueChange={(set) =>
                  point(() => api.pointAt(bot.id, set === SHARED ? null : set))
                }
              >
                <SelectTrigger id={`account-${bot.id}`} className="w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={SHARED}>the shared account</SelectItem>
                    {accounts.map((it) => (
                      <SelectItem key={it.set} value={it.set}>
                        {it.account ?? it.set}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>

        <div className="flex w-85 flex-none flex-col gap-2.5 max-sm:w-full">
          <Apparatus>Lately</Apparatus>
          <div>
            <RuledRow
              label="Lane"
              value={bot.lane ? wordFor(bot.lane.status) : "idle"}
              tone={
                bot.lane?.status === "RUNNING"
                  ? "text-running"
                  : bot.lane?.status === "WAITING_APPROVAL"
                    ? "text-waiting"
                    : "text-muted-foreground"
              }
            />
            <RuledRow label="Runs, 30 d" value={grouped(bot.runs ?? 0)} />
            <RuledRow label="Saved, 30 d" value={grouped(bot.saved ?? 0)} />
            <RuledRow
              label="Failed, 30 d"
              value={grouped(bot.failed ?? 0)}
              tone={(bot.failed ?? 0) > 0 ? "text-destructive" : undefined}
              last
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => navigate("/tasks")}>New task</Button>
            <Button
              variant="outline"
              disabled={starting}
              onClick={() => act(() => api.start({ botId: bot.id }))}
            >
              {starting ? "Queueing" : "Run now"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
